import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyMessages, sendCourseMessage } from '../../services/courseMessages';
import { getMergedEnrollments } from '../../utils/enrollmentStore';
import './Messages.css';

function formatDate(timestamp) {
  if (!timestamp?.toDate) return '';
  return timestamp.toDate().toLocaleString();
}

export default function Messages() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedCourseId = searchParams.get('courseId') || null;
  const [messages, setMessages] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composeCourseId, setComposeCourseId] = useState(preselectedCourseId || '');
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = useCallback(() => {
    if (!user?.uid) {
      setMessages([]);
      setEnrolledCourses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    Promise.all([
      getMyMessages(user.uid),
      getMergedEnrollments(user.uid),
    ])
      .then(([msgList, enrollList]) => {
        setMessages(msgList || []);
        setEnrolledCourses(enrollList || []);
      })
      .catch(() => setError('Could not load messages.'))
      .finally(() => setLoading(false));
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (preselectedCourseId) setComposeCourseId(preselectedCourseId);
  }, [preselectedCourseId]);

  const handleSend = (e) => {
    e.preventDefault();
    const courseId = (composeCourseId || '').trim();
    const text = (composeText || '').trim();
    if (!courseId || !text || !user) return;
    const course = enrolledCourses.find((c) => String(c.id) === String(courseId));
    const courseTitle = course?.title || courseId;
    setSendError('');
    setSending(true);
    sendCourseMessage({
      courseId,
      courseTitle,
      userId: user.uid,
      userEmail: user.email || '',
      userName: user.name || '',
      message: text,
    })
      .then(() => {
        setComposeText('');
        load();
      })
      .catch(() => setSendError('Could not send message. Please try again.'))
      .finally(() => setSending(false));
  };

  return (
    <div className="user-messages-page">
      <div className="user-messages-inner">
        <h1 className="user-messages-title">Messages</h1>
        <p className="user-messages-desc">
          Your messaging inbox. Send and view all your conversations with course creators here. Ask questions, confirm payment, or get support.
        </p>
        {error && <p className="user-messages-error" role="alert">{error}</p>}

        <section className="user-messages-compose">
          <h2 className="user-messages-compose-title">Message course owner</h2>
          {user ? (
            <form onSubmit={handleSend} className="user-messages-compose-form">
              {sendError && <p className="user-messages-send-error" role="alert">{sendError}</p>}
              <label className="user-messages-compose-label">
                <span>Course</span>
                <select
                  value={composeCourseId}
                  onChange={(e) => setComposeCourseId(e.target.value)}
                  className="user-messages-compose-select"
                  required
                >
                  <option value="">Select a course</option>
                  {enrolledCourses.map((c) => (
                    <option key={c.id} value={c.id}>{c.title || c.id}</option>
                  ))}
                </select>
              </label>
              {enrolledCourses.length === 0 && (
                <p className="user-messages-compose-hint">Enroll in a course first to message the owner. Go to <Link to="/my-learning">My Learning</Link> or browse courses.</p>
              )}
              <label className="user-messages-compose-label">
                <span>Your message</span>
                <textarea
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  className="user-messages-compose-textarea"
                  placeholder="Ask a question, confirm payment, or get support..."
                  rows={4}
                  required
                  disabled={enrolledCourses.length === 0}
                />
              </label>
              <button type="submit" className="user-messages-compose-btn" disabled={sending || enrolledCourses.length === 0}>
                {sending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          ) : (
            <p className="user-messages-compose-hint"><Link to="/login">Log in</Link> to send a message.</p>
          )}
        </section>

        <section className="user-messages-threads">
          <h2 className="user-messages-threads-title">Your messages</h2>
          {loading ? (
            <p className="user-messages-loading">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="user-messages-empty">No messages yet. Send a message above to start a conversation with the course owner.</p>
          ) : (
            <ul className="user-messages-list">
              {messages.map((m) => (
                <li key={m.id} className="user-messages-card">
                  <div className="user-messages-card-head">
                    <Link to={`/course/${m.courseId}`} className="user-messages-card-course">{m.courseTitle || 'Course'}</Link>
                    <span className="user-messages-card-date">{formatDate(m.createdAt)}</span>
                  </div>
                  <div className="user-messages-card-body">
                    <p className="user-messages-card-you"><strong>You said:</strong> {m.message || '—'}</p>
                    {m.adminReply ? (
                      <p className="user-messages-card-reply"><strong>Course owner replied:</strong> {m.adminReply}</p>
                    ) : (
                      <p className="user-messages-card-pending">Waiting for course owner to reply.</p>
                    )}
                  </div>
                  <Link to={`/course/${m.courseId}`} className="user-messages-card-link">View course</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
